import React from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Avatar,
  IconButton,
  Skeleton,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Flag as GoalIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CompleteIcon,
  RadioButtonUnchecked as InProgressIcon
} from '@mui/icons-material';

/**
 * Goals Widget
 * Shows family goals and progress
 */
const GoalsWidget = ({ goals = [], loading = false }) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={32} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="rectangular" width="100%" height={6} sx={{ my: 1 }} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#c62828', mr: 2 }}>
            <GoalIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Family Goals
          </Typography>
        </Box>
        <IconButton
          component={Link}
          to="/goals"
          color="primary"
          size="small"
        >
          <ArrowIcon />
        </IconButton>
      </Box>

      {/* Content */}
      {!goals || goals.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <GoalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No goals set yet
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            component={Link}
            to="/goals"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Set a family goal
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {goals.slice(0, 5).map((goal, index) => {
            const progress = goal.progress || 0;
            const isComplete = goal.status === 'completed' || progress >= 100;

            return (
              <ListItem
                key={goal.id}
                component={Link}
                to="/goals"
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom: index < Math.min(goals.length, 5) - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ mr: 1 }}>
                        {isComplete ? (
                          <CompleteIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        ) : (
                          <InProgressIcon sx={{ fontSize: 20, color: 'action.active' }} />
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                        {goal.title || goal.name}
                      </Typography>
                      <Chip
                        label={`${progress}%`}
                        size="small"
                        color={isComplete ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        sx={{ my: 1, height: 6, borderRadius: 3 }}
                        color={isComplete ? 'success' : 'primary'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {goal.description?.substring(0, 60)}
                        {goal.description?.length > 60 && '...'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default GoalsWidget;
